import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of, forkJoin, timer } from 'rxjs';
import { map, catchError, tap, switchMap, shareReplay, distinctUntilChanged } from 'rxjs/operators';
import { GuardsService } from './guards.service';
import { PositionsService } from './positions.service';
import { UsersService } from './users.service';
import { DevicesService } from './devices.service';
import { CheckinsService } from './checkins.service';
import { DeviceRequestsService } from './device-requests.service';
import { AccessRequestsService } from './access-requests.service';
import { AuthService } from './auth.service';
import { filterByCompanyId } from '../utils/company-filter.util';

export interface CachedData<T> {
  data: T;
  timestamp: number;
  isStale: boolean;
}

export interface AppStatus {
  isOnline: boolean;
  lastSync: Date | null;
  syncInProgress: boolean;
  error: string | null;
}

export interface DashboardStats {
  totalGuards: number;
  activeGuards: number;
  inactiveGuards: number;
  totalPositions: number;
  activePositions: number;
  inactivePositions: number;
  totalUsers: number;
  activeUsers: number;
  totalDevices: number;
  activeDevices: number;
  todayCheckIns: number;
  last7DaysCheckIns: number;
  pendingRequests: number;
  totalAccessRequests: number;
  pendingAccessRequests: number;
  totalSupervisors?: number;
  activeSchedules?: number;
  totalPatrolRoutes?: number;
  activePatrolRoutes?: number;
  totalCheckpoints?: number;
  patrolsCompletedToday?: number;
  patrolsInProgress?: number;
  patrolsToday?: number;
  complianceRate?: number;
  positionsWithoutCheckIn24h?: number;
}

@Injectable({
  providedIn: 'root',
})
export class StatusManageService {
  private readonly CACHE_DURATION = 5 * 60 * 1000; // 5 minutos
  private readonly STALE_DURATION = 2 * 60 * 1000; // 2 minutos para considerar stale

  // Cache de dados
  private guardsCache$ = new BehaviorSubject<CachedData<any[]> | null>(null);
  private positionsCache$ = new BehaviorSubject<CachedData<any[]> | null>(null);
  private usersCache$ = new BehaviorSubject<CachedData<any[]> | null>(null);
  private devicesCache$ = new BehaviorSubject<CachedData<any[]> | null>(null);
  private checkinsCache$ = new BehaviorSubject<CachedData<any[]> | null>(null);
  private deviceRequestsCache$ = new BehaviorSubject<CachedData<any[]> | null>(null);
  private accessRequestsCache$ = new BehaviorSubject<CachedData<any[]> | null>(null);

  // Estado da aplicação
  private appStatusSubject$ = new BehaviorSubject<AppStatus>({
    isOnline: navigator.onLine,
    lastSync: null,
    syncInProgress: false,
    error: null,
  });

  // Cache de estatísticas do dashboard
  private dashboardStatsCache$ = new BehaviorSubject<CachedData<DashboardStats> | null>(null);

  // Observables públicos
  public readonly guards$ = this.guardsCache$.asObservable().pipe(
    map((cache) => cache?.data || []),
    distinctUntilChanged(),
  );

  public readonly positions$ = this.positionsCache$.asObservable().pipe(
    map((cache) => cache?.data || []),
    distinctUntilChanged(),
  );

  public readonly users$ = this.usersCache$.asObservable().pipe(
    map((cache) => cache?.data || []),
    distinctUntilChanged(),
  );

  public readonly devices$ = this.devicesCache$.asObservable().pipe(
    map((cache) => cache?.data || []),
    distinctUntilChanged(),
  );

  public readonly checkins$ = this.checkinsCache$.asObservable().pipe(
    map((cache) => cache?.data || []),
    distinctUntilChanged(),
  );

  public readonly deviceRequests$ = this.deviceRequestsCache$.asObservable().pipe(
    map((cache) => cache?.data || []),
    distinctUntilChanged(),
  );

  public readonly accessRequests$ = this.accessRequestsCache$.asObservable().pipe(
    map((cache) => cache?.data || []),
    distinctUntilChanged(),
  );

  public readonly appStatus$ = this.appStatusSubject$.asObservable();
  public readonly dashboardStats$ = this.dashboardStatsCache$.asObservable().pipe(
    map((cache) => cache?.data || this.getEmptyStats()),
    distinctUntilChanged(),
  );

  // Indicadores de sincronização
  public readonly isSyncing$ = this.appStatusSubject$.pipe(
    map((status) => status.syncInProgress),
    distinctUntilChanged(),
  );

  public readonly lastSync$ = this.appStatusSubject$.pipe(
    map((status) => status.lastSync),
    distinctUntilChanged(),
  );

  // Delay inicial antes da primeira sincronização (ms) — dá tempo ao UI renderizar
  private readonly INITIAL_SYNC_DELAY = 3000;

  constructor(
    private guardsService: GuardsService,
    private positionsService: PositionsService,
    private usersService: UsersService,
    private devicesService: DevicesService,
    private checkinsService: CheckinsService,
    private deviceRequestsService: DeviceRequestsService,
    private accessRequestsService: AccessRequestsService,
    private authService: AuthService,
  ) {
    this.initializeCache();
    this.setupOnlineStatusListener();
    this.setupAutoSync();
  }

  private initializeCache(): void {
    // Carregar dados do localStorage imediatamente (sem chamadas HTTP)
    this.loadFromLocalStorage();
    // A primeira sincronização HTTP é feita pelo setupAutoSync com delay
  }

  private setupOnlineStatusListener(): void {
    window.addEventListener('online', () => {
      this.updateAppStatus({ isOnline: true });
      this.syncAllData().subscribe(); // Sincronizar quando voltar online
    });

    window.addEventListener('offline', () => {
      this.updateAppStatus({ isOnline: false });
    });
  }

  private setupAutoSync(): void {
    // Aguarda INITIAL_SYNC_DELAY antes da primeira sync para não bloquear o render inicial.
    // Depois repete a cada CACHE_DURATION (5 min).
    timer(this.INITIAL_SYNC_DELAY, this.CACHE_DURATION)
      .pipe(
        switchMap(() => {
          if (
            this.appStatusSubject$.value.isOnline &&
            !this.appStatusSubject$.value.syncInProgress
          ) {
            return this.syncAllData();
          }
          return of(null);
        }),
      )
      .subscribe();
  }

  private updateAppStatus(updates: Partial<AppStatus>): void {
    const currentStatus = this.appStatusSubject$.value;
    this.appStatusSubject$.next({ ...currentStatus, ...updates });
  }

  private getEmptyStats(): DashboardStats {
    return {
      totalGuards: 0,
      activeGuards: 0,
      inactiveGuards: 0,
      totalPositions: 0,
      activePositions: 0,
      inactivePositions: 0,
      totalUsers: 0,
      activeUsers: 0,
      totalDevices: 0,
      activeDevices: 0,
      todayCheckIns: 0,
      last7DaysCheckIns: 0,
      pendingRequests: 0,
      totalAccessRequests: 0,
      pendingAccessRequests: 0,
      totalSupervisors: 0,
      activeSchedules: 0,
      totalPatrolRoutes: 0,
      activePatrolRoutes: 0,
      totalCheckpoints: 0,
      patrolsCompletedToday: 0,
      patrolsInProgress: 0,
      patrolsToday: 0,
      complianceRate: 0,
      positionsWithoutCheckIn24h: 0,
    };
  }

  private isCacheValid(cache: CachedData<any> | null): boolean {
    if (!cache) return false;
    const now = Date.now();
    return now - cache.timestamp < this.CACHE_DURATION;
  }

  private isCacheStale(cache: CachedData<any> | null): boolean {
    if (!cache) return true;
    const now = Date.now();
    return now - cache.timestamp > this.STALE_DURATION;
  }

  private createCachedData<T>(data: T): CachedData<T> {
    return {
      data,
      timestamp: Date.now(),
      isStale: false,
    };
  }

  private updateCache<T>(cacheSubject: BehaviorSubject<CachedData<T> | null>, data: T): void {
    const cachedData = this.createCachedData(data);
    cacheSubject.next(cachedData);
    this.saveToLocalStorage();
  }

  private loadFromLocalStorage(): void {
    try {
      const cached = localStorage.getItem('smv_cache');
      if (cached) {
        const parsed = JSON.parse(cached);
        const now = Date.now();

        // Verificar se o cache ainda é válido
        if (parsed.timestamp && now - parsed.timestamp < this.CACHE_DURATION) {
          if (parsed.guards) this.guardsCache$.next(parsed.guards);
          if (parsed.positions) this.positionsCache$.next(parsed.positions);
          if (parsed.users) this.usersCache$.next(parsed.users);
          if (parsed.devices) this.devicesCache$.next(parsed.devices);
          if (parsed.checkins) this.checkinsCache$.next(parsed.checkins);
          if (parsed.deviceRequests) this.deviceRequestsCache$.next(parsed.deviceRequests);
          if (parsed.accessRequests) this.accessRequestsCache$.next(parsed.accessRequests);
          if (parsed.dashboardStats) this.dashboardStatsCache$.next(parsed.dashboardStats);

          this.updateAppStatus({ lastSync: new Date(parsed.timestamp) });
        }
      }
    } catch (error) {
      console.error('Erro ao carregar cache do localStorage:', error);
    }
  }

  private saveToLocalStorage(): void {
    try {
      const cacheData = {
        timestamp: Date.now(),
        guards: this.guardsCache$.value,
        positions: this.positionsCache$.value,
        users: this.usersCache$.value,
        devices: this.devicesCache$.value,
        checkins: this.checkinsCache$.value,
        deviceRequests: this.deviceRequestsCache$.value,
        accessRequests: this.accessRequestsCache$.value,
        dashboardStats: this.dashboardStatsCache$.value,
      };

      localStorage.setItem('smv_cache', JSON.stringify(cacheData));
    } catch (error) {
      console.error('Erro ao salvar cache no localStorage:', error);
    }
  }

  // Métodos públicos para sincronização
  public syncAllData(): Observable<any> {
    if (this.appStatusSubject$.value.syncInProgress) {
      return of(null);
    }

    this.updateAppStatus({ syncInProgress: true, error: null });

    return forkJoin({
      guards: this.syncGuards(),
      positions: this.syncPositions(),
      users: this.syncUsers(),
      devices: this.syncDevices(),
      checkins: this.syncCheckins(),
      deviceRequests: this.syncDeviceRequests(),
      accessRequests: this.syncAccessRequests(),
    }).pipe(
      tap(() => {
        this.updateAppStatus({
          syncInProgress: false,
          lastSync: new Date(),
          error: null,
        });
        this.calculateDashboardStats();
      }),
      catchError((error) => {
        this.updateAppStatus({
          syncInProgress: false,
          error: 'Erro na sincronização dos dados',
        });
        return of(null);
      }),
    );
  }

  public syncGuards(): Observable<any[]> {
    return this.guardsService.getAllWithResponse().pipe(
      map((response) => {
        // Tratar status 304 como sucesso - dados não modificados
        if (response.status === 304) {
          
          const cached = this.guardsCache$.value?.data || [];
          return filterByCompanyId(cached, this.authService);
        }

        // Extrair dados da resposta
        const responseBody = response.body;
        const data = Array.isArray(responseBody) ? responseBody : (responseBody as any)?.data || [];
        // Filtrar por company_id como camada extra de segurança
        return filterByCompanyId(data, this.authService);
      }),
      tap((guards) => this.updateCache(this.guardsCache$, guards)),
      catchError((error) => {
        const cached = this.guardsCache$.value?.data || [];
        return of(filterByCompanyId(cached, this.authService));
      }),
    );
  }

  public syncPositions(): Observable<any[]> {
    return this.positionsService.getAllWithResponse().pipe(
      map((response) => {
        // Tratar status 304 como sucesso - dados não modificados
        if (response.status === 304) {
          const cached = this.positionsCache$.value?.data || [];
          return filterByCompanyId(cached, this.authService);
        }

        // Extrair dados da resposta
        const responseBody = response.body;
        const data = Array.isArray(responseBody) ? responseBody : (responseBody as any)?.data || [];
        // Filtrar por company_id como camada extra de segurança
        return filterByCompanyId(data, this.authService);
      }),
      tap((positions) => this.updateCache(this.positionsCache$, positions)),
      catchError((error) => {
        console.error('Erro ao sincronizar positions:', error);
        const cached = this.positionsCache$.value?.data || [];
        return of(filterByCompanyId(cached, this.authService));
      }),
    );
  }

  public syncUsers(): Observable<any[]> {
    return this.usersService.getAllWithResponse().pipe(
      map((response) => {
        // Tratar status 304 como sucesso - dados não modificados
        if (response.status === 304) {
       
          const cached = this.usersCache$.value?.data || [];
          return filterByCompanyId(cached, this.authService);
        }

        // Extrair dados da resposta
        const responseBody = response.body;
        const data = Array.isArray(responseBody) ? responseBody : (responseBody as any)?.data || [];
        // Filtrar por company_id como camada extra de segurança
        return filterByCompanyId(data, this.authService);
      }),
      tap((users) => this.updateCache(this.usersCache$, users)),
      catchError((error) => {
        console.error('Erro ao sincronizar users:', error);
        const cached = this.usersCache$.value?.data || [];
        return of(filterByCompanyId(cached, this.authService));
      }),
    );
  }

  public syncDevices(): Observable<any[]> {
    return this.devicesService.getAllWithResponse().pipe(
      map((response) => {
        // Tratar status 304 como sucesso - dados não modificados
        if (response.status === 304) {
          
          const cached = this.devicesCache$.value?.data || [];
          return filterByCompanyId(cached, this.authService);
        }

        // Extrair dados da resposta - usar response.body como data diretamente
        const responseBody = response.body;
        const data = Array.isArray(responseBody) ? responseBody : (responseBody as any)?.data || [];
        

        // Filtrar por company_id como camada extra de segurança
        return filterByCompanyId(data, this.authService);
      }),
      tap((devices) => {
    
        this.updateCache(this.devicesCache$, devices);
      }),
      catchError((error) => {
        console.error('Erro ao sincronizar devices:', error);
        const cached = this.devicesCache$.value?.data || [];
        return of(filterByCompanyId(cached, this.authService));
      }),
    );
  }

  public syncCheckins(): Observable<any[]> {
    return this.checkinsService.getAllWithResponse().pipe(
      map((response) => {
        // Tratar status 304 como sucesso - dados não modificados
        if (response.status === 304) {
          
          const cached = this.checkinsCache$.value?.data || [];
          return filterByCompanyId(cached, this.authService);
        }

        // Extrair dados da resposta - usar response.body como data diretamente
        const responseBody = response.body;
        const data = Array.isArray(responseBody) ? responseBody : (responseBody as any)?.data || [];


        // Filtrar por company_id como camada extra de segurança
        return filterByCompanyId(data, this.authService);
      }),
      tap((checkins) => {
  
        this.updateCache(this.checkinsCache$, checkins);
      }),
      catchError((error) => {
        
        const cached = this.checkinsCache$.value?.data || [];
        return of(filterByCompanyId(cached, this.authService));
      }),
    );
  }

  public syncDeviceRequests(): Observable<any[]> {
    return this.deviceRequestsService.getAllRequestsWithResponse().pipe(
      map((response) => {
        // Tratar status 304 como sucesso - dados não modificados
        if (response.status === 304) {
        
          const cached = this.deviceRequestsCache$.value?.data || [];
          return filterByCompanyId(cached, this.authService);
        }

        // Extrair dados da resposta - usar response.body como data diretamente
        const responseBody = response.body;
        const data = Array.isArray(responseBody) ? responseBody : (responseBody as any)?.data || [];
      
        // Filtrar por company_id como camada extra de segurança
        return filterByCompanyId(data, this.authService);
      }),
      tap((requests) => {
        
        this.updateCache(this.deviceRequestsCache$, requests);
      }),
      catchError((error) => {
        console.error('Erro ao sincronizar device requests:', error);
        const cached = this.deviceRequestsCache$.value?.data || [];
        return of(filterByCompanyId(cached, this.authService));
      }),
    );
  }

  public syncAccessRequests(): Observable<any[]> {
    return this.accessRequestsService.getAllWithResponse().pipe(
      map((response) => {
        // Tratar status 304 como sucesso - dados não modificados
        if (response.status === 304) {
        
          const cached = this.accessRequestsCache$.value?.data || [];
          return filterByCompanyId(cached, this.authService);
        }

        // Extrair dados da resposta - usar response.body como data diretamente
        const responseBody = response.body;
        const data = Array.isArray(responseBody) ? responseBody : (responseBody as any)?.data || [];
      
        // Filtrar por company_id como camada extra de segurança
        return filterByCompanyId(data, this.authService);
      }),
      tap((requests) => {
        
        this.updateCache(this.accessRequestsCache$, requests);
      }),
      catchError((error) => {
        console.error('Erro ao sincronizar access requests:', error);
        const cached = this.accessRequestsCache$.value?.data || [];
        return of(filterByCompanyId(cached, this.authService));
      }),
    );
  }

  private calculateDashboardStats(): void {
    const guards = this.guardsCache$.value?.data || [];
    const positions = this.positionsCache$.value?.data || [];
    const users = this.usersCache$.value?.data || [];
    const devices = this.devicesCache$.value?.data || [];
    const checkins = this.checkinsCache$.value?.data || [];
    const deviceRequests = this.deviceRequestsCache$.value?.data || [];
    const accessRequests = this.accessRequestsCache$.value?.data || [];

    

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const todayCheckIns = checkins.filter((checkin: any) => {
      const checkinDate = new Date(checkin.created_at);
      checkinDate.setHours(0, 0, 0, 0);
      return checkinDate.getTime() === today.getTime();
    }).length;

    const pendingDeviceRequests = deviceRequests.filter(
      (req: any) => req.status === 'pending',
    ).length;
    const pendingAccessRequests = accessRequests.filter(
      (req: any) => req.status === 'pending',
    ).length;

    // Contar supervisores (role ou user_type === 'supervisor')
    const supervisors = users.filter(
      (u: any) => u.role === 'supervisor' || u.user_type === 'supervisor',
    );
    const totalSupervisors = supervisors.length;

    // Por agora, activeSchedules será 0 até implementarmos a sincronização de schedules
    const activeSchedules = 0;

    const activeGuardsCount = guards.filter((g: any) => g.status === 'active').length;
    const activePositionsCount = positions.filter((p: any) => p.is_active).length;

    const stats: DashboardStats = {
      totalGuards: guards.length,
      activeGuards: activeGuardsCount,
      inactiveGuards: guards.length - activeGuardsCount,
      totalPositions: positions.length,
      activePositions: activePositionsCount,
      inactivePositions: positions.length - activePositionsCount,
      totalUsers: users.length,
      activeUsers: users.filter((u: any) => u.is_active).length,
      totalDevices: devices.length,
      activeDevices: devices.filter((d: any) => d.isActive).length,
      todayCheckIns,
      last7DaysCheckIns: 0, // Será calculado se necessário
      pendingRequests: pendingDeviceRequests,
      totalAccessRequests: accessRequests.length,
      pendingAccessRequests,
      totalSupervisors,
      activeSchedules,
      totalPatrolRoutes: 0,
      activePatrolRoutes: 0,
      totalCheckpoints: 0,
      patrolsCompletedToday: 0,
      patrolsInProgress: 0,
      patrolsToday: 0,
      complianceRate: 0,
      positionsWithoutCheckIn24h: 0,
    };

   
    this.updateCache(this.dashboardStatsCache$, stats);
  }

  // Métodos para obter dados específicos
  public getGuards(): Observable<any[]> {
    const cache = this.guardsCache$.value;
    if (this.isCacheValid(cache)) {
      return of(cache!.data);
    }

    if (this.isCacheStale(cache)) {
      // Retornar dados stale enquanto sincroniza em background
      this.syncGuards().subscribe();
      return of(cache?.data || []);
    }

    return this.syncGuards();
  }

  public getPositions(): Observable<any[]> {
    const cache = this.positionsCache$.value;
    if (this.isCacheValid(cache)) {
      return of(cache!.data);
    }

    if (this.isCacheStale(cache)) {
      this.syncPositions().subscribe();
      return of(cache?.data || []);
    }

    return this.syncPositions();
  }

  public getUsers(): Observable<any[]> {
    const cache = this.usersCache$.value;
    if (this.isCacheValid(cache)) {
      return of(cache!.data);
    }

    if (this.isCacheStale(cache)) {
      this.syncUsers().subscribe();
      return of(cache?.data || []);
    }

    return this.syncUsers();
  }

  public getDevices(): Observable<any[]> {
    const cache = this.devicesCache$.value;
    if (this.isCacheValid(cache)) {
      return of(cache!.data);
    }

    if (this.isCacheStale(cache)) {
      this.syncDevices().subscribe();
      return of(cache?.data || []);
    }

    return this.syncDevices();
  }

  public getCheckins(): Observable<any[]> {
    const cache = this.checkinsCache$.value;
    if (this.isCacheValid(cache)) {
      return of(cache!.data);
    }

    if (this.isCacheStale(cache)) {
      this.syncCheckins().subscribe();
      return of(cache?.data || []);
    }

    return this.syncCheckins();
  }

  public getDeviceRequests(): Observable<any[]> {
    const cache = this.deviceRequestsCache$.value;
    if (this.isCacheValid(cache)) {
      return of(cache!.data);
    }

    if (this.isCacheStale(cache)) {
      this.syncDeviceRequests().subscribe();
      return of(cache?.data || []);
    }

    return this.syncDeviceRequests();
  }

  public getAccessRequests(): Observable<any[]> {
    const cache = this.accessRequestsCache$.value;
    if (this.isCacheValid(cache)) {
      return of(cache!.data);
    }

    if (this.isCacheStale(cache)) {
      this.syncAccessRequests().subscribe();
      return of(cache?.data || []);
    }

    return this.syncAccessRequests();
  }

  public getDashboardStats(): Observable<DashboardStats> {
    const cache = this.dashboardStatsCache$.value;
    if (this.isCacheValid(cache)) {
      return of(cache!.data);
    }

    // Recalcular estatísticas
    this.calculateDashboardStats();
    return of(this.dashboardStatsCache$.value?.data || this.getEmptyStats());
  }

  // Métodos de utilidade
  public clearCache(): void {
    this.guardsCache$.next(null);
    this.positionsCache$.next(null);
    this.usersCache$.next(null);
    this.devicesCache$.next(null);
    this.checkinsCache$.next(null);
    this.deviceRequestsCache$.next(null);
    this.accessRequestsCache$.next(null);
    this.dashboardStatsCache$.next(null);

    localStorage.removeItem('smv_cache');
  }

  public forceSync(): Observable<any> {
    return this.syncAllData();
  }

  public recalculateStats(): void {
    this.calculateDashboardStats();
  }

  public isDataStale(): boolean {
    const caches = [
      this.guardsCache$.value,
      this.positionsCache$.value,
      this.usersCache$.value,
      this.devicesCache$.value,
      this.checkinsCache$.value,
      this.deviceRequestsCache$.value,
      this.accessRequestsCache$.value,
    ];

    return caches.some((cache) => this.isCacheStale(cache));
  }

  public getCacheInfo(): any {
    return {
      guards: {
        hasData: !!this.guardsCache$.value,
        isStale: this.isCacheStale(this.guardsCache$.value),
        timestamp: this.guardsCache$.value?.timestamp,
      },
      positions: {
        hasData: !!this.positionsCache$.value,
        isStale: this.isCacheStale(this.positionsCache$.value),
        timestamp: this.positionsCache$.value?.timestamp,
      },
      users: {
        hasData: !!this.usersCache$.value,
        isStale: this.isCacheStale(this.usersCache$.value),
        timestamp: this.usersCache$.value?.timestamp,
      },
      devices: {
        hasData: !!this.devicesCache$.value,
        isStale: this.isCacheStale(this.devicesCache$.value),
        timestamp: this.devicesCache$.value?.timestamp,
      },
      checkins: {
        hasData: !!this.checkinsCache$.value,
        isStale: this.isCacheStale(this.checkinsCache$.value),
        timestamp: this.checkinsCache$.value?.timestamp,
      },
      deviceRequests: {
        hasData: !!this.deviceRequestsCache$.value,
        isStale: this.isCacheStale(this.deviceRequestsCache$.value),
        timestamp: this.deviceRequestsCache$.value?.timestamp,
      },
      accessRequests: {
        hasData: !!this.accessRequestsCache$.value,
        isStale: this.isCacheStale(this.accessRequestsCache$.value),
        timestamp: this.accessRequestsCache$.value?.timestamp,
      },
    };
  }
}
